import  { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import Layout from '@/components/layout/layout'
import MenuSubCategories from '@/components/menus/menu-subcategories'
import { Link, useParams } from 'react-router-dom'
import { spanishToEntity } from '@/utils/entity-convert'
import { Entities } from '@/entities/api/entities'
import {
	AdvancedProductFilter,
	FilteredProductsResponse,
} from '@/entities/api/products/product-filters'
import Toast from '@/components/elements/toast'
import { filterMultiCriteria } from '@/services/api/products/products-filter'
import SubcategoryHeroSection from '@/components/heros/subcategory-hero'
import ProductSimpleSlider from '@/components/sliders/products-simple-slider'
import { useSelector } from 'react-redux'
import { RootState } from '@/store/store'
import Guarantees from '@/components/layout/parts/guarantees'
import CustomersEntity from '@/entities/api/customers/customer'

const SubCategory = ({
	customer,
	token,
}: {
	customer: CustomersEntity | null
	token: string | null
}) => {
	const { type: typeParam, category: encodedCategory } = useParams<{
		type: string
		category: string
	}>()

	const category = decodeURIComponent(encodedCategory ?? '').replace(/-/g, ' ')

	const type: Entities = useMemo(
		() => spanishToEntity(typeParam ?? '') as Entities,
		[typeParam]
	)

	const categories = useSelector(
		(state: RootState) => state.category.categories
	)

	const categoryData = useMemo(() => {
		if (!category || !categories.length) return null

		const foundCategory = categories.find(
			(c) => c.name?.toLowerCase() === category.toLowerCase()
		)

		return foundCategory
			? {
					id: foundCategory._id,
					name: foundCategory.name,
					isValid: true,
			  }
			: {
					id: undefined,
					name: category,
					isValid: false,
			  }
	}, [category, categories])

	const [products, setProducts] = useState<FilteredProductsResponse>()
	const [isLoading, setIsLoading] = useState(false)

	const lastFetchedFiltersRef = useRef<string>('')

	const initialFilters = useMemo(
		(): AdvancedProductFilter => ({
			categoryType: {
				type,
				categoryName: categoryData?.name,
				...(categoryData?.id && { categoryId: categoryData.id }),
			},
			limit: 12,
			page: 1,
			onlyActive: true,
		}),
		[type, categoryData?.id, categoryData?.name]
	)

	const [currentFilters, setCurrentFilters] =
		useState<AdvancedProductFilter>(initialFilters)

	const getProductsByFilters = useCallback(
		async (filters: AdvancedProductFilter) => {
			if (isLoading) return

			const filtersKey = JSON.stringify(filters)
			if (lastFetchedFiltersRef.current === filtersKey) {
				return
			}

			lastFetchedFiltersRef.current = filtersKey
			setIsLoading(true)

			try {
				const response = await filterMultiCriteria(filters)

				if ([400, 401, 409, 404].includes(response?.status ?? 0)) {
					Toast({
						type: 'error',
						message: 'No se pudieron cargar los productos de esta categoría',
					})
					return
				}

				setProducts(response?.data)
			} catch (error) {
				Toast({
					type: 'error',
					message: 'Error de conexión. Intenta nuevamente.',
				})
				console.error('Error fetching products:', error)
			} finally {
				setIsLoading(false)
			}
		},
		[isLoading]
	)

	const handleLoadMore = useCallback(
		async (nextPage: number): Promise<void> => {
			if (isLoading) return

			const loadMoreFilters = {
				...currentFilters,
				page: nextPage,
			}

			setIsLoading(true)

			try {
				const response = await filterMultiCriteria(loadMoreFilters)

				if ([400, 401, 409, 404].includes(response?.status ?? 0)) {
					Toast({
						type: 'error',
						message: 'Error al cargar más productos',
					})
					return
				}

				if (response?.data?.products) {
					setProducts((prevProducts) => {
						if (!prevProducts) return response.data

						return {
							...response.data,
							products: [...prevProducts.products, ...response.data.products],
							currentPage: response.data.currentPage,
						}
					})
				}
			} catch (error) {
				Toast({
					type: 'error',
					message: 'Error al cargar más productos',
				})
				throw error
			} finally {
				setIsLoading(false)
			}
		},
		[currentFilters, isLoading]
	)

	const handleFiltersChange = useCallback(
		(newFilters: AdvancedProductFilter) => {
			const updatedFilters: AdvancedProductFilter = {
				...newFilters,
				categoryType: {
					categoryId: categoryData?.id,
					type: type,
					categoryName: categoryData?.name,
				},
				limit: 12,
				page: 1,
				onlyActive: true,
			}

			setCurrentFilters(updatedFilters)
			lastFetchedFiltersRef.current = ''
			getProductsByFilters(updatedFilters)
		},
		[type, categoryData?.id, categoryData?.name]
	)

	useEffect(() => {
		if (type && categoryData?.name && categories.length > 0) {
			const filtersKey = JSON.stringify(initialFilters)
			if (lastFetchedFiltersRef.current !== filtersKey) {
				getProductsByFilters(initialFilters)
			}
		}
	}, [type, categoryData?.name, categoryData?.id, categories.length])

	useEffect(() => {
		if (categoryData) {
			setCurrentFilters(initialFilters)
		}
	}, [categoryData?.id, categoryData?.name, type])

	const isValidCategory = useMemo(
		() => categoryData?.isValid ?? false,
		[categoryData?.isValid]
	)

	const displayName = useMemo(
		() => categoryData?.name ?? category ?? 'Categoría',
		[categoryData?.name, category]
	)

	if (!type || !category) {
		return (
			<Layout customer={customer} token={token}>
				<div className='min-h-[60vh] flex items-center justify-center'>
					<div className='text-center space-y-4'>
						<h2 className='text-2xl font-bold text-gray-800'>
							Categoría no encontrada
						</h2>
						<p className='text-gray-600'>
							La categoría que buscas no existe o ha sido movida.
						</p>
						<Link
							to='/'
							className='inline-block bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl'
						>
							Volver al Inicio
						</Link>
					</div>
				</div>
			</Layout>
		)
	}

	return (
		<Layout customer={customer} token={token}>
			<div className='w-full px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6 lg:py-8'>
				<div className='mb-6 lg:mb-8'>
					<SubcategoryHeroSection
						subcategoryName={displayName}
						isNew={isValidCategory}
						isPopular={!!products?.totalCount && products.totalCount > 10}
					/>
				</div>

				<div className='w-full flex flex-col relative'>
					<div className='flex justify-center items-start'>
						<div className='w-full max-w-none px-0 md:px-2 lg:px-4 xl:px-8'>
							<div className='w-full flex relative'>
								<MenuSubCategories
									selectedCategoryId={categoryData?.id}
									currentType={type}
									onFiltersChange={handleFiltersChange}
									currentFilters={currentFilters}
								/>

								<div className='w-full flex flex-col min-w-0'>
									<div className='space-y-4 sm:space-y-6 lg:space-y-8'>
										<ProductSimpleSlider
											filteredProducts={products}
											enableInfiniteScroll={true}
											onLoadMore={handleLoadMore}
											isLoading={isLoading}
											customerId={customer?._id || ''}
											customer={customer}
										/>
									</div>

									<div className='mt-12 lg:mt-16 pt-8 border-t border-gray-200'>
										<div className='text-center'>
											<h3 className='text-lg sm:text-xl lg:text-2xl font-bold text-gray-800 mb-2'>
												¿No encontraste lo que buscabas?
											</h3>
											<p className='text-sm sm:text-base text-gray-600 mb-6'>
												Explora más categorías o usa nuestros filtros avanzados
											</p>
											<Link
												to='/'
												className='bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl'
											>
												Ver Todas las Categorías
											</Link>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<Guarantees />
			</div>
		</Layout>
	)
}

export default SubCategory
